import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { RELEASED_ON, APP_VERSION } from "@/lib/version";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { user, login, verifyCode } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfa, setMfa] = useState(null);
  const [code, setCode] = useState("");
  const [remember, setRemember] = useState(true);
  const [forgot, setForgot] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const result = await login(email, password);
      if (result?.mfa_required) {
        setMfa(result);
        if (result.emailed === false)
          toast.warning("The email service is busy — if the code doesn't arrive, try again in a minute");
        else toast.success(`Code sent to ${result.sent_to}`);
        return;
      }
      toast.success("Welcome back to Dhunna Jewellers");
      navigate("/");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await verifyCode(mfa.challenge, code.trim(), remember);
      toast.success("Welcome back to Dhunna Jewellers");
      navigate("/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      setForgot(false);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col lg:flex-row">
      <div className="relative hidden flex-1 lg:block">
        <img
          src="/brand/login-bride.jpg"
          alt="Dhunna Jewellers bridal gold jewellery"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-black/10" />
        <div className="fade-up absolute bottom-16 left-14 right-14 text-white">
          <div className="gold-rule w-20" />
          <h1 className="mt-6 font-invoice text-5xl leading-tight">
            Sales Portal
          </h1>
          <p
            className="mt-3 text-[11px] tracking-wider text-white/60"
            data-testid="login-version"
          >
            v{APP_VERSION} · released {RELEASED_ON}
          </p>
        </div>
      </div>

      <div className="flex w-full items-center justify-center bg-white px-6 py-16 lg:w-[480px]">
        <div className="fade-up w-full max-w-sm">
          <img
            src="/brand/logo.png"
            alt="Dhunna Jewellers"
            className="h-20 w-auto object-contain"
          />
          <div className="mt-4 font-invoice text-2xl">DHUNNA JEWELLERS</div>
          <div className="mt-1 text-[10px] font-semibold tracking-[0.35em] text-[#B38F4E]">
            GOLD &nbsp;|&nbsp; DIAMOND &nbsp;|&nbsp; SILVER
          </div>
          <p
            className="mt-4 text-[11px] tracking-wider text-muted-foreground lg:hidden"
            data-testid="login-version-mobile"
          >
            Sales Portal · v{APP_VERSION}
          </p>
          <h2 className="mt-10 text-lg font-bold">Sign in to continue</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Use your staff account credentials.
          </p>

          <form onSubmit={mfa ? submitCode : forgot ? sendReset : submit} className="mt-8 space-y-5" data-testid="login-form">
            {mfa && (
              <div className="border-l-2 border-[#C5A059] bg-[#FBF8F2] p-3 text-sm" data-testid="mfa-step">
                We emailed a 6-digit code to <strong>{mfa.sent_to}</strong>. It expires in{" "}
                {mfa.expires_in_minutes} minutes.
              </div>
            )}
            {forgot && !mfa && (
              <div className="border-l-2 border-[#C5A059] bg-[#FBF8F2] p-3 text-sm" data-testid="forgot-step">
                Enter your email and we will send you a link to set a new password.
              </div>
            )}
            {!mfa && (
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs uppercase tracking-wider">
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@dhunna.com.au"
                  data-testid="login-email-input"
                  className="h-11 rounded-none"
                />
              </div>
            )}
            {!mfa && !forgot && (
              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs uppercase tracking-wider">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                    className="h-11 rounded-none pr-11"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    aria-pressed={showPassword}
                    data-testid="toggle-password-visibility"
                    className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-muted-foreground transition-colors duration-150 hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            )}
            {mfa && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="code" className="text-xs uppercase tracking-wider">
                    6-digit code
                  </Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                    data-testid="mfa-code-input"
                    className="tabular h-11 rounded-none text-center text-2xl tracking-[0.4em]"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    data-testid="remember-device-checkbox"
                  />
                  Remember this device for 30 days
                </label>
              </>
            )}
            {error && (
              <p
                className="border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                data-testid="login-error"
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="h-11 w-full rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
            >
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mfa ? "Verify code" : forgot ? "Send reset link" : "Sign In"}
            </Button>
            <button
              type="button"
              onClick={() => {
                setError("");
                if (mfa) {
                  setMfa(null);
                  setCode("");
                } else {
                  setForgot(!forgot);
                }
              }}
              className="w-full text-center text-sm text-muted-foreground underline hover:text-[#B38F4E]"
              data-testid="login-secondary-action"
            >
              {mfa ? "Back to sign in" : forgot ? "Back to sign in" : "Forgot password?"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
